/**
 * Admin QA defaults and recommended keys for Custom events send validation and presets.
 */

/** Subscription tier strings (match Brooklyn `SUBSCRIPTION_TIERS`) */
export const QA_SUBSCRIPTION_TIER_ANNUAL = 'Annual'
export const QA_SUBSCRIPTION_TIER_GAIA_PLUS = 'Gaia+'

export interface CustomEventQaFixture {
  /** UI field keys that must be non-empty before send */
  recommendedAdminFieldKeys: readonly string[]
  /** Values merged into payload root (camelCase), not `fields` */
  payloadRootFieldKeys: readonly string[]
  /** Default string values per UI field key */
  defaultFieldValues: Record<string, string>
}

const BONUS_ELIGIBILITY_SAMPLE = JSON.stringify([
  {
    id: 101,
    ecommerceSku: 'SKU001',
    provider: 'gaia_bonus',
    ecommerceSkuKey: 'gaia_bonus-SKU001',
    marketplaceCouponCode: 'COUPON1',
    marketplaceCouponCodeExpiration: '2025-12-31T00:00:00.000Z',
    entitledTo: 'Bonus Product Title',
    contentSetId: 290,
    startDate: '06-25-2026',
    endDate: '07-02-2026',
  },
])

const PLAN_CHANGE_ELIGIBILITY_SAMPLE = JSON.stringify([
  {
    id: 201,
    ecommerceSku: 'PLAN-CHANGE-SKU',
    provider: 'shopify',
    ecommerceSkuKey: 'shopify-PLAN-CHANGE-SKU',
    marketplaceCouponCode: 'COUPON2',
    marketplaceCouponCodeExpiration: '2026-12-31T00:00:00.000Z',
    entitledTo: 'Plan Change Bonus',
    contentSetId: 280,
    startDate: '06-01-2026',
    endDate: '',
  },
])

export const CUSTOM_EVENT_QA_FIXTURES: Record<string, CustomEventQaFixture> = {
  PASSWORD_RESET: {
    recommendedAdminFieldKeys: ['password_reset_url', 'registration_language'],
    payloadRootFieldKeys: [],
    defaultFieldValues: {
      password_reset_url: 'https://www.gaia.com/password/reset?token=admin-qa-test',
      registration_language: 'en',
      first_name: 'QA',
    },
  },
  NEW_USER_PASSWORD_RESET: {
    recommendedAdminFieldKeys: ['password_reset_url', 'registration_language'],
    payloadRootFieldKeys: [],
    defaultFieldValues: {
      password_reset_url: 'https://www.gaia.com/password/reset?token=admin-qa-new-user',
      registration_language: 'en',
    },
  },
  LOGIN_CODE: {
    recommendedAdminFieldKeys: ['magic_link_url', 'email_mode', 'registration_language'],
    payloadRootFieldKeys: [],
    defaultFieldValues: {
      magic_link_url: 'https://www.gaia.com/login/magic?token=admin-qa',
      email_mode: 'link-only',
      web_app_server: 'https://www.gaia.com',
      registration_language: 'en',
      digits_code: '',
      first_name: 'QA',
    },
  },
  GIFT_SUBSCRIPTION_RECIPIENT_DELIVERY: {
    recommendedAdminFieldKeys: [
      'gifterName',
      'recipientEmail',
      'recipientFirstName',
      'recipientLastName',
      'registrationLanguage',
      'deliveryDate',
      'expiresAt',
      'tokenURL',
      'planName',
      'token',
    ],
    payloadRootFieldKeys: [],
    defaultFieldValues: {
      gifterName: 'Admin QA Gifter',
      recipientFirstName: 'Gift',
      recipientLastName: 'Recipient',
      registrationLanguage: 'en',
      deliveryDate: '',
      expiresAt: '',
      tokenURL: 'https://www.gaia.com/gift/redeem?token=admin-qa',
      message: 'Admin QA gift delivery test',
      planName: 'Annual Gift',
      token: 'admin-qa-token',
    },
  },
  GIFT_SUBSCRIPTION_PURCHASER_RECEIPT: {
    recommendedAdminFieldKeys: ['first_name', 'last_name', 'registration_language', 'plan_name'],
    payloadRootFieldKeys: [],
    defaultFieldValues: {
      first_name: 'Pat',
      last_name: 'Buyer',
      registration_language: 'en',
      plan_name: 'Annual Gift',
      country: 'US',
      currency: 'USD',
    },
  },
  GIFT_SUBSCRIPTION_REDEEMED: {
    recommendedAdminFieldKeys: ['first_name', 'registration_language'],
    payloadRootFieldKeys: [],
    defaultFieldValues: {
      first_name: 'Redeemer',
      registration_language: 'en',
    },
  },
  SHARE_VIDEO_LEAD: {
    recommendedAdminFieldKeys: [
      'form_name',
      'registration_language',
      'shared_video_id',
      'url',
      'new_share_url',
      'share_watch_link_clicked',
      'prospect_behavior_segment',
    ],
    payloadRootFieldKeys: ['everflowTransactionId', 'utm'],
    defaultFieldValues: {
      form_name: 'SHARE_VIDEO_LEAD_1',
      registration_language: 'en',
      shared_video_id: '12345',
      new_share_url: 'https://www.gaia.com/share/new',
      url: 'https://www.gaia.com/share/watch',
      share_watch_link_clicked: 'false',
      prospect_behavior_segment: 'br',
      prospect_email_comm_br: 'true',
      everflowTransactionId: '',
      utm: 'utm_source=share&utm_medium=email',
    },
  },
  ALACARTE_PURCHASE_MADE: {
    recommendedAdminFieldKeys: [
      'language',
      'registration_language',
      'is_new_user',
      'includes_content',
      'entitled_ecommerce_sku_keys',
    ],
    payloadRootFieldKeys: [],
    defaultFieldValues: {
      registration_language: 'en',
      language: 'en',
      first_name: 'Buyer',
      is_new_user: 'true',
      password_reset_url: 'https://www-stg.example.gaia.com/password-reset?token=admin-qa',
      includes_content: 'true',
      entitled_ecommerce_sku_keys: '["SHOPIFY-JOE_BLACK_SKU"]',
    },
  },
  BONUS_GRANTED: {
    recommendedAdminFieldKeys: [
      'language',
      'includes_content',
      'is_new_user',
      'entitled_ecommerce_sku_keys',
      'ecommerce_sku_mapping_bonus_eligibilities',
    ],
    payloadRootFieldKeys: [],
    defaultFieldValues: {
      registration_language: 'en',
      language: 'en',
      plan_name: 'Annual',
      first_name: 'Member',
      includes_content: 'true',
      is_new_user: 'false',
      entitled_ecommerce_sku_keys: '["gaia_bonus-SKU001"]',
      ecommerce_sku_mapping_bonus_eligibilities: BONUS_ELIGIBILITY_SAMPLE,
    },
  },
  BONUS_SKU_SUBSCRIPTION_SIGNUP: {
    recommendedAdminFieldKeys: [
      'language',
      'plan_name',
      'subscription_tier',
      'trial_period',
      'gaia_product_sku',
      'next_bill_amount',
      'paid_through_date',
      'currency',
    ],
    payloadRootFieldKeys: [],
    defaultFieldValues: {
      registration_language: 'en',
      language: 'es',
      plan_name: 'Annual',
      subscription_tier: QA_SUBSCRIPTION_TIER_ANNUAL,
      trial_period: 'true',
      gaia_product_sku: 'annual-plan-sku',
      next_bill_amount: '9.99',
      paid_through_date: '2027-06-25',
      currency: 'USD',
    },
  },
  BONUS_SKU_SUBSCRIPTION_PLAN_CHANGE: {
    recommendedAdminFieldKeys: [
      'language',
      'plan_name',
      'subscription_tier',
      'trial_period',
      'gaia_product_sku',
      'next_bill_amount',
      'paid_through_date',
      'currency',
      'includes_content',
      'creation_source',
    ],
    payloadRootFieldKeys: [],
    defaultFieldValues: {
      registration_language: 'en',
      language: 'en',
      plan_name: 'Pro',
      subscription_tier: QA_SUBSCRIPTION_TIER_GAIA_PLUS,
      trial_period: 'false',
      gaia_product_sku: 'pro-plan-sku',
      next_bill_amount: '19.99',
      paid_through_date: '2027-06-25',
      currency: 'USD',
      includes_content: 'true',
      creation_source: 'zuora',
      entitled_ecommerce_sku_keys: '["shopify-PLAN-CHANGE-SKU"]',
      ecommerce_sku_mapping_bonus_eligibilities: PLAN_CHANGE_ELIGIBILITY_SAMPLE,
    },
  },
}

export function getCustomEventQaFixture (templateId: string): CustomEventQaFixture | undefined {
  return CUSTOM_EVENT_QA_FIXTURES[templateId]
}

export function getQaRecommendedAdminFieldKeys (templateId: string): string[] {
  const fixture = getCustomEventQaFixture(templateId)
  return fixture ? [...fixture.recommendedAdminFieldKeys] : []
}

export function getQaPayloadRootFieldKeys (templateId: string): string[] {
  const fixture = getCustomEventQaFixture(templateId)
  return fixture ? [...fixture.payloadRootFieldKeys] : []
}

export function getQaDefaultFieldValuesForTemplate (
  templateId: string,
  memberEmail?: string,
): Record<string, string> {
  const fixture = getCustomEventQaFixture(templateId)
  if (!fixture) {
    return {}
  }
  const values = { ...fixture.defaultFieldValues }
  if (templateId === 'GIFT_SUBSCRIPTION_RECIPIENT_DELIVERY') {
    const today = formatGiftDate(new Date())
    values.deliveryDate = values.deliveryDate || today
    values.expiresAt = values.expiresAt || today
    values.recipientEmail = memberEmail || 'recipient@example.com'
  }
  return values
}

function formatGiftDate (date: Date): string {
  const month = String(date.getMonth() + 1)
  const day = String(date.getDate())
  const year = date.getFullYear()
  return `${month}-${day}-${year}`
}
